import { useNavigate } from "react-router-dom";
import { MapPin, ChevronRight, Navigation, CalendarDays } from "lucide-react";
import { ResponsiveOverlay } from "@/components/ui/ResponsiveOverlay";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import type { ExploreMode } from "./ExploreModeToggle";
import type { ExploreResultItem } from "@/types/exploreResult";
import { formatDistanceKm } from "@/lib/map/formatDistance";
import { FavoriteButton } from "@/components/favorites/FavoriteButton";
import { ShareButton } from "@/components/share/ShareButton";
import type { FavoriteKind } from "@/lib/favorites/types";

interface ExplorePreviewSheetProps {
  item: ExploreResultItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: ExploreMode;
}

function getGoogleMapsUrl(lat?: number | null, lng?: number | null, address?: string | null): string {
  if (lat && lng) return `https://www.google.com/maps?q=${lat},${lng}`;
  if (address) return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  return "#";
}

export function ExplorePreviewSheet({ item, open, onOpenChange }: ExplorePreviewSheetProps) {
  const navigate = useNavigate();

  if (!item) return null;

  const { kind, title, subtitle, shortDescription: description, image, badges, dateLabel, _distanceKm, href, slug, isTrip, priceText, spotsInfo, address, lat, lng } = item;

  const distance = _distanceKm != null ? formatDistanceKm(_distanceKm) : null;
  const favKind: FavoriteKind = kind === "activity" ? "activity" : kind === "producer" ? "producer" : "place";
  const shareKind = kind === "activity" ? "activity" as const : kind === "producer" ? "producer" as const : "place" as const;

  // Primary CTA based on kind
  const primaryCta = (() => {
    if (isTrip) {
      return {
        label: "Vägbeskrivning",
        icon: Navigation,
        action: () => window.open(getGoogleMapsUrl(lat, lng, address), "_blank"),
      };
    }
    if (kind === "activity") {
      return {
        label: "Visa aktivitet",
        icon: ChevronRight,
        action: () => { onOpenChange(false); navigate(href); },
      };
    }
    if (kind === "producer") {
      return {
        label: "Visa gård",
        icon: ChevronRight,
        action: () => { onOpenChange(false); navigate(href); },
      };
    }
    return {
      label: "Visa plats",
      icon: ChevronRight,
      action: () => { onOpenChange(false); navigate(href); },
    };
  })();

  return (
    <ResponsiveOverlay
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      desktopMaxWidth="md"
      className="p-0"
    >
      <div className="flex items-center justify-between pt-3 pb-1 px-5 md:hidden">
        <div className="flex-1" />
        <div className="flex items-center gap-1">
          <ShareButton kind={shareKind} slug={slug} title={title} size="sm" />
          <FavoriteButton
            kind={favKind}
            entityId={item.id}
            slug={slug || undefined}
            title={title}
            imagePath={image || undefined}
            size="sm"
          />
        </div>
      </div>

      {/* Desktop header with share/fav */}
      <div className="hidden md:flex items-center justify-between px-6 pt-2 pb-1">
        <div className="flex-1" />
        <div className="flex items-center gap-1">
          <ShareButton kind={shareKind} slug={slug} title={title} size="sm" />
          <FavoriteButton
            kind={favKind}
            entityId={item.id}
            slug={slug || undefined}
            title={title}
            imagePath={image || undefined}
            size="sm"
          />
        </div>
      </div>

      {/* Image */}
      <div className="relative w-full aspect-video bg-muted overflow-hidden">
        {image ? (
          <img src={image} alt={title} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-muted">
            <MapPin className="h-10 w-10 text-muted-foreground/40" />
          </div>
        )}

        {dateLabel && (
          <div className="absolute top-3 left-3">
            <div className="bg-background/95 backdrop-blur-sm rounded-lg px-3 py-1.5 shadow-sm">
              <span className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                <CalendarDays className="h-3.5 w-3.5" />
                {dateLabel}
              </span>
            </div>
          </div>
        )}

        {distance && (
          <div className="absolute top-3 right-3">
            <span className="text-xs font-medium bg-background/90 backdrop-blur-sm text-foreground px-2 py-1 rounded-full flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              {distance}
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="px-5 pt-4 pb-2 space-y-3">
        <div className="flex flex-wrap gap-1.5">
          {badges.map((b) => (
            <Badge key={b} variant="secondary" className="text-xs font-normal">
              {b}
            </Badge>
          ))}
          {isTrip && (
            <Badge variant="outline" className="text-xs font-normal">
              Ingen bokning krävs
            </Badge>
          )}
          {spotsInfo && (
            <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-800">
              {spotsInfo}
            </Badge>
          )}
        </div>

        <div>
          <h3 className="text-lg font-semibold text-foreground line-clamp-2">{title}</h3>
          {subtitle && (
            <p className="text-sm text-muted-foreground mt-0.5 line-clamp-1">{subtitle}</p>
          )}
        </div>

        {description && (
          <p className="text-sm text-foreground/80 line-clamp-2">{description}</p>
        )}
      </div>

      {/* Single CTA — lightweight preview */}
      <div className="px-5 pt-2 pb-5">
        <Button className="w-full gap-2" size="lg" onClick={primaryCta.action}>
          <primaryCta.icon className="h-4 w-4" />
          {primaryCta.label}
        </Button>
      </div>
    </ResponsiveOverlay>
  );
}
